import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Loader2, Plus, X, Mail, Phone } from "lucide-react";

const STATUSES = ["Applied", "Interviewing", "Offer", "Hired", "Rejected"];
const COLORS = {
  Applied: { bg: "#DCE9F0", color: "#1F4863" },
  Interviewing: { bg: "#FCE7C9", color: "#9A6020" },
  Offer: { bg: "#FADED7", color: "#9A3412" },
  Hired: { bg: "#D8F3DC", color: "#1B4332" },
  Rejected: { bg: "#F4F1DE", color: "#57534E" },
};

export default function Recruitment() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/candidates");
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/candidates/${id}/status`, { status });
    load();
  };

  const byStatus = STATUSES.reduce((acc, s) => {
    acc[s] = items.filter((c) => c.status === s);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Hiring Pipeline</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Recruitment</h1>
        </div>
        <button onClick={() => setShow(true)} data-testid="add-candidate-button"
                className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
          <Plus className="w-4 h-4" /> Add Candidate
        </button>
      </header>

      {loading ? <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="recruitment-kanban">
          {STATUSES.map((s) => {
            const c = COLORS[s];
            return (
              <div key={s} className="space-y-3">
                <div className="flex items-center justify-between px-3 py-2 rounded-md" style={{ background: c.bg, color: c.color }}>
                  <div className="text-xs font-bold uppercase tracking-wider">{s}</div>
                  <div className="text-xs font-bold">{byStatus[s].length}</div>
                </div>
                <div className="space-y-2" data-testid={`column-${s.toLowerCase()}`}>
                  {byStatus[s].map((cand) => (
                    <div key={cand.id} className="bg-white rounded-md p-3 border border-stone-200 shadow-sm">
                      <div className="font-semibold text-sm">{cand.full_name}</div>
                      <div className="text-xs text-stone-500 mt-0.5">{cand.position}</div>
                      <div className="text-xs text-stone-500">{cand.department_name}</div>
                      <div className="mt-2 flex items-center gap-2 text-xs text-stone-600">
                        <span>{cand.experience_years}yr</span>
                        <span>·</span>
                        <span>{formatINR(cand.expected_ctc)}</span>
                      </div>
                      <select value={cand.status} onChange={(e) => setStatus(cand.id, e.target.value)}
                              data-testid={`set-status-${cand.id}`}
                              className="mt-2 text-xs px-2 py-1 border border-stone-200 rounded w-full bg-white">
                        {STATUSES.map((x) => <option key={x}>{x}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {show && <AddCandidateModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function AddCandidateModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", position: "", department_name: "",
    experience_years: 0, expected_ctc: 0, status: "Applied", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.post("/candidates", {
      ...form, experience_years: Number(form.experience_years),
      expected_ctc: Number(form.expected_ctc),
    });
    setBusy(false); onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4" data-testid="candidate-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">Add Candidate</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <In label="Full Name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required />
          <In label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
          <In label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <In label="Position" value={form.position} onChange={(v) => setForm({ ...form, position: v })} required />
          <In label="Department" value={form.department_name} onChange={(v) => setForm({ ...form, department_name: v })} />
          <In label="Experience (years)" type="number" value={form.experience_years} onChange={(v) => setForm({ ...form, experience_years: v })} />
          <In label="Expected CTC ₹" type="number" value={form.expected_ctc} onChange={(v) => setForm({ ...form, expected_ctc: v })} />
          <label><div className="kpi-label mb-1.5">Status</div>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
              {STATUSES.map((x) => <option key={x}>{x}</option>)}
            </select>
          </label>
          <button type="submit" disabled={busy} data-testid="save-candidate-button"
                  className="col-span-2 py-2 mt-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

function In({ label, type = "text", value, onChange, required }) {
  return <label><div className="kpi-label mb-1.5">{label}</div>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
           className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
  </label>;
}
